import React, { useCallback } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import DragIndicatorIcon from 'icons/drag-indicator.svg';
import CrossIcon from 'icons/ic-close.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import Tooltip from 'components/application/Tooltip';

export default function SortableCollectionInput({
  collectionErrors,
  index,
  isDeletable,
  onChange,
  onDelete,
  removeErrorStyling,
  value,
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: index });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'collections.SortableCollectionInput',
  });

  const fieldRef = useCallback((node) => {
    node !== null && node.focus();
  }, []);

  const handleFocus = () => {
    if (collectionErrors.errorList[index]) {
      return removeErrorStyling(index);
    }
  };

  return (
    <li
      className={classNames(
        'sortable-collection__member-wrapper tw-group tw-relative',
        isDragging && 'tw-z-[1]',
      )}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className='sortable-collection__member'>
        <div
          className={classNames(
            'tw-flex tw-h-9 tw-w-5 tw-touch-none tw-items-center tw-justify-center group-hover:tw-visible hover-hover:tw-invisible',
            isDragging ? 'tw-cursor-grabbing' : 'tw-cursor-grab',
          )}
          data-element-name={`drag-handle-${index}`}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon
            className='[&_path]:tw-fill-grey-700'
            height={12}
            width={8}
          />
        </div>
        <div className='sortable-collection__input-container'>
          <input
            className={classNames(
              'sortable-collection__input field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
              collectionErrors.errorList[index]?.fieldHighlighted ?
                'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
              : 'tw-border-grey-300 placeholder:tw-text-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
            )}
            id={`sortable-collection__input-${index}`}
            name='name'
            onChange={(event) => onChange(event, { index })}
            onFocus={handleFocus}
            ref={fieldRef}
            type='text'
            value={value}
          />
        </div>
        <div
          className={classNames(
            'sortable-collection__icon-delete',
            isDeletable ? 'tw-bg-red-600' : 'tw-bg-grey-300',
          )}
          onClick={isDeletable ? () => onDelete(index) : () => {}}
        >
          <ConditionalWrapper
            condition={!isDeletable}
            wrapper={(children) => (
              <Tooltip
                className='-tw-top-1'
                placement='top'
                tooltip={i18nT('tooltip')}
                trigger='hover'
              >
                {children}
              </Tooltip>
            )}
          >
            <CrossIcon className='sortable-collection__icon-delete-inner [&_polygon]:tw-fill-white' />
          </ConditionalWrapper>
        </div>
      </div>
    </li>
  );
}

SortableCollectionInput.propTypes = {
  collectionErrors: PropTypes.exact({
    errorList: PropTypes.array.isRequired,
    isFallback: PropTypes.bool.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isDeletable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  value: PropTypes.string,
};
